import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ArrowUpRight, BarChart3 } from "lucide-react";
import { useCockpitSession } from "@/hooks/use-cockpit-session";
import { AccountManagerCard } from "@/components/cockpit/AccountManagerCard";
import { ElectionCountdownCard } from "@/components/cockpit/ElectionCountdownCard";
import { ModuleFocusCard } from "@/components/cockpit/ModuleFocusCard";
import { NachleseNote } from "@/components/cockpit/NachleseNote";
import { OpenItemsCard } from "@/components/cockpit/OpenItemsCard";
import { WahlkalenderShortcutCard } from "@/components/cockpit/WahlkalenderShortcutCard";
import { getMyCockpitHome } from "@/lib/cockpit-home.functions";
import { homeSections, selectOpenItems } from "@/lib/cockpit-home";

export const Route = createFileRoute("/_cockpit/cockpit/")({
  head: () => ({
    meta: [
      { title: "Cockpit – creategy" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: CockpitIndex,
});

/**
 * Ticket KN4: Die Startseite wiederholt die Seitenleiste nicht mehr. Sie richtet
 * sich nach derselben Phase, die seit Ticket KN2 die Seitenleiste steuert, und
 * zeigt ausschließlich Abschnitte, die etwas zu sagen haben.
 */
function CockpitIndex() {
  const session = useCockpitSession();
  const fetchHome = useServerFn(getMyCockpitHome);
  // Ticket KN4: EIN gebündelter Aufruf für alles, was nicht schon in der
  // Cockpit-Antwort steht (Countdown, Modul, Nachlese-Text).
  const { data: home } = useQuery({
    queryKey: ["cockpit", "home"],
    queryFn: () => fetchHome(),
    staleTime: 5 * 60 * 1000,
  });

  const firstName = session.firstName?.trim() || null;
  const org = session.organizationName?.trim() || null;
  const areas = session.areas;
  const reportUrl = areas.report ? session.mocoReportUrl?.trim() || null : null;
  const openItems = selectOpenItems(session.onboarding.items);

  const sections = homeSections({
    data: home ?? null,
    openItems,
    reportUrl,
    hasAccountManager: Boolean(session.accountManager),
    // Ticket WK32: Verweiskarte zum Wahlkalender bei aktiver Zuweisung.
    hasWahlkalender: session.modules.wahlkalender,
  });

  const intro = org
    ? `Willkommen im Cockpit von ${org}. Hier finden Sie ${
        areas.downloads
          ? "Ihre Kurse, Fragebögen, Downloads und Rechnungen"
          : "Ihre Kurse und Lerninhalte"
      }.`
    : "Hier finden Sie Ihre Kurse und Lerninhalte.";

  return (
    <div className="mx-auto w-full min-w-0 max-w-6xl">
      <h1 className="break-words font-display text-3xl font-extrabold tracking-tight text-primary sm:text-4xl md:text-5xl">
        {firstName ? `Guten Tag, ${firstName}.` : "Willkommen im Cockpit."}
      </h1>
      <p className="mt-3 max-w-2xl break-words text-muted-foreground">{intro}</p>

      <div className="mt-10 grid gap-6 xl:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="space-y-6">
          {sections.map((section) => {
            switch (section) {
              case "countdown":
                return <ElectionCountdownCard key={section} countdown={home?.countdown ?? null} />;
              case "module":
                return home?.module ? (
                  <ModuleFocusCard key={section} module={home.module} />
                ) : null;
              case "wahlkalender":
                return <WahlkalenderShortcutCard key={section} />;
              case "open_items":
                return <OpenItemsCard key={section} selection={openItems} />;
              case "nachlese":
                return home?.nachleseText ? (
                  <NachleseNote key={section} text={home.nachleseText} />
                ) : null;
              case "report":
                return reportUrl ? (
                  <a
                    key={section}
                    href={reportUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-start justify-between gap-4 rounded-lg border border-border bg-card p-6 transition-colors hover:bg-secondary"
                  >
                    <div className="flex items-start gap-3">
                      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-secondary text-primary">
                        <BarChart3 className="h-5 w-5" aria-hidden />
                      </div>
                      <div className="min-w-0">
                        <p className="break-words font-display text-lg font-extrabold text-primary">
                          Projektbericht
                        </p>
                        <p className="mt-1 max-w-md text-sm text-muted-foreground">
                          Den aktuellen Stand Ihrer Projekte finden Sie in Ihrem Projektbericht.
                        </p>
                        <span className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-brand-button px-3 py-1.5 text-xs font-semibold text-[#212121]">
                          Projektbericht ansehen
                          <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
                        </span>
                      </div>
                    </div>
                  </a>
                ) : null;
              default:
                return null;
            }
          })}
        </div>

        {sections.includes("account_manager") && session.accountManager ? (
          <div className="xl:sticky xl:top-20 xl:self-start">
            <AccountManagerCard manager={session.accountManager} />
          </div>
        ) : null}
      </div>
    </div>
  );
}
